import * as _ from 'lodash'
import MapPin from './MapPin'
import { MapShape } from './MapShape'
import Group from './Group'
import MapUserLocation from './MapUserLocation'
import Network from './Network'

class Mission {
    id = -1

    name = 'unnamed mission'

    active = 1

    organization_id = 3

    created_by = null

    settings = {}

    starts_at = null

    ends_at = null

    created_at = null

    updated_at = null

    newStaleUsers = []

    groups = []

    networks = []

    object_name = 'Mission'

    collection = []

    get(name) {
        return this[name]
    }

    set(values) {
        _.forEach(values, (v, k) => {
            if (values[k] !== undefined) {
                this[k] = values[k]
            }
        })
    }

    add(object) {
        this.collection.push(object)
    }

    updateFromData(data) {
        const typeMap = { pins: MapPin, shapes: MapShape, user_locations: MapUserLocation }
        this.newStaleUsers = []

        // Map Objects
        _.forEach(typeMap, (className, type) => {
            _.forEach(data[type], (objectData) => {
                const index = _.findIndex(this.collection, (m) => {
                    const identifier = m.get('identifier')
                    return m[identifier] === objectData[identifier] && m instanceof className
                })

                objectData.mission_id = this.get('id')
                const isUserLocation = className === MapUserLocation

                let model
                let settings
                if (index === -1) {
                    // This is a new map object (create it)
                    // eslint-disable-next-line new-cap
                    model = new className(objectData)
                    this.add(model)
                    settings = _.clone(this.getSettingsForObject(model))
                } else {
                    // This is an existing map object (update it)
                    model = this.collection[index]
                    settings = _.clone(model.settings)

                    model.set(objectData)
                }

                if (isUserLocation) {
                    const shouldBeStale = this.getStaleStatusOfUser(model)
                    const userIsInactive = this.getInactiveStatusOfUser(model)
                    if (!userIsInactive && shouldBeStale && !settings.stale) {
                        this.newStaleUsers = [...this.get('newStaleUsers'), model]
                    }
                    settings.stale = shouldBeStale
                    settings.visible = Boolean(model.latitude)
                }

                // Update the map object with its old settings
                _.each(settings, (v, k) => {
                    if (v !== null) {
                        model.settings[k] = v
                    }
                    this.updateSettingForObject(model, k, v)
                })
            })
        })

        // Groups
        _.forEach(data.groups, (groupData) => {
            const groups = this.get('groups')
            const existingGroup = _.findIndex(groups, (g) => g.id === groupData.id)
            groupData.mission_id = this.get('id')
            let group
            let settings
            if (existingGroup === -1) {
                group = new Group(groupData)
                groups.push(group)
                settings = this.getSettingsForObject(group)
            } else {
                group = groups[existingGroup]
                group.set(groupData)
                settings = { ...group.settings }
            }

            _.each(settings, (v, k) => {
                if (v !== null) {
                    group.settings[k] = v
                }
                this.updateSettingForObject(group, k, v)
            })
        })
    }

    updateNetworks(data) {
        _.forEach(data, (networkData) => {
            const networks = this.get('networks')
            const existingNetwork = _.findIndex(networks, (n) => n.id === networkData.id)
            const backhaulerLocation = this.getUserLocations().find(
                (userLocation) => userLocation.gid === networkData.backhauler.gid
            )
            networkData.mission_id = this.get('id')
            networkData.backhaulerLocation = backhaulerLocation || null
            let network
            if (existingNetwork === -1) {
                network = new Network(networkData)
                networks.push(network)
                network.updateMessages(networkData.messages)
                if (!this.getActiveNetwork()) {
                    this.setActiveNetwork(network)
                    network.markMessagesAsRead()
                }
            } else {
                network = networks[existingNetwork]
                network.set(networkData)
                network.updateMessages(networkData.messages)
            }
        })
    }

    clearAllMapObjects() {
        this.groups = []
        this.collection = []
    }

    clearMapObject(object) {
        let modelToRemove = object

        if (object instanceof MapUserLocation) {
            this.clearGroupUserLocations([object])
        } else if (object instanceof Group) {
            this.clearGroup(object)
            modelToRemove = object.userLocations
        }

        this.remove(modelToRemove)
    }

    clearGroup(group) {
        const missionGroups = this.get('groups')
        const groups = missionGroups.filter((missionGroup) => missionGroup.id !== group.id)

        this.groups = groups
        this.clearGroupUserLocations(group.userLocations)
    }

    clearGroupUserLocations(userLocations) {
        const groups = this.get('groups')
        const updatedGroups = groups
            .map((group) => {
                const groupUserLocations = group.userLocations
                const withoutUserLocations = _.differenceBy(groupUserLocations, userLocations, 'gid')
                const locationGids = withoutUserLocations.map((location) => location.gid)
                group.userLocations = withoutUserLocations
                group.location_gids = locationGids

                return group
            })
            .filter((group) => group.userLocations.length !== 0)

        this.groups = updatedGroups
    }

    clearNetworks() {
        this.networks = []
    }

    getStaleStatusOfUser(user) {
        const t = window.moment.unix(new Date()) / 1000
        const lastUserUpdate = t - window.moment.unix(user.timestamp)._i
        const staleTimeout = 60 * 5 * 1000

        return lastUserUpdate >= staleTimeout
    }

    getInactiveStatusOfUser(user) {
        const t = window.moment.unix(new Date()) / 1000
        const lastUserUpdate = t - window.moment.unix(user.timestamp)._i
        const inactiveTimeout = 24 * 60 * 60 * 1000

        return lastUserUpdate >= inactiveTimeout
    }

    getPins() {
        return _.filter(this.collection, (m) => m instanceof MapPin)
    }

    getUserLocations() {
        return _.filter(this.collection, (m) => m instanceof MapUserLocation)
    }

    getStaleUsers() {
        return this.get('newStaleUsers')
    }

    getShapes() {
        return _.filter(this.collection, (m) => m instanceof MapShape)
    }

    getGroups() {
        const userLocationsMapping = this.getUserLocationsMapping()
        return this.get('groups').map((group) => {
            const userLocations = group.location_gids.map((gid) => userLocationsMapping[gid])
            group.userLocations = _.sortBy(userLocations, [(user) => user.isStale(), (user) => user.user.callsign])

            const visible = (userLocation) => userLocation.isVisible()
            group.areAllUsersHidden = !group.userLocations.some(visible)

            return group
        })
    }

    getNetworks() {
        return this.get('networks')
    }

    getActiveNetwork() {
        const activeNetwork = _.filter(this.get('networks'), (network) => network.isActive())[0]
        return activeNetwork || null
    }

    setActiveNetwork(network) {
        _.forEach(this.get('networks'), (n) => (n === network ? n.setActive(true) : n.setActive(false)))
    }

    getUserLocationsMapping() {
        const map = {}
        this.getUserLocations().forEach((userLocation) => {
            map[userLocation.gid] = userLocation
        })
        return map
    }

    getSettingsForObject(object) {
        const settings = this.get('settings')
        const identifier = object.get('identifier')
        const key = `${object.get('object_name')}_${object[identifier]}`
        if (settings[key]) {
            return settings[key]
        }

        return {}
    }

    updateSettingForObject(object, property, value) {
        const settings = this.get('settings')
        const identifier = object.get('identifier')
        const key = `${object.get('object_name')}_${object[identifier]}`
        if (!settings[key]) {
            settings[key] = {}
        }
        settings[key][property] = value
        this.settings = settings
    }
}

export default Mission
